import React, { useState, useEffect, forwardRef, useCallback } from 'react';
import {
  Box,
  Button,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Snackbar,
  Alert,
  Grid,
  Checkbox,
  Card,
  CardContent,
  Autocomplete,
  Chip,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import AddIcon from '@mui/icons-material/Add';
import { getAuthToken } from '../../utils/authUtils';
import { useSelector } from 'react-redux';
import { selectUserFacilities } from '../../redux/slices/facilitiesSlice';
import { getAllFacilities } from '../../services/userService';
import { useUserData } from '../../hooks/useUserData';
import { ORDER_TYPE_STATUS_FLOW } from '../../types/statusFlows';
import { OrderType, ORDER_TYPES } from '../../types/enums';
import { ProcedureService } from '../../services/procedureService';
import { Patient } from '../../types/Patient';
import { ProcedureStatus, TestDefinition } from '../../types/procedures';
import { DatePicker } from '@mui/x-date-pickers';

// Import modular components
import { LabOrdersGrid } from './components/LabOrdersGrid';
import { OrderTypeFields } from './components/OrderTypeFields';
import { OrderActions } from './components/OrderActions';
import { ImagingReportDialog } from './components/ImagingReportDialog';
import { ResendConfirmDialog } from './components/ResendConfirmDialog';
import { ReferralForm } from './components/ReferralForm';
import { RequisitionForm } from './components/RequisitionForm';

// Import hooks and utilities
import { useProcedures } from './hooks/useProcedures';
import { 
  getDateStringFromOrderDate, 
  getInitialFormState,
  PRIORITY_OPTIONS,
  CHECKUP_OPTIONS,
  FormState,
} from './utils/orderUtils';
import { sendOrderViaIntegration, sendHL7Order } from './utils/hl7Utils';
import MacroTextBox from '../utils/MacroTextBox';

interface ProceduresProps {
  patientId: string;
  encounterId?: string;
  externalOpenNewOrder?: (handler: () => void) => void;
  compact?: boolean; // When true, displays in compact mode for embedded use
}

const procedureService = new ProcedureService();

// Styled Components - Modern Theme
const ModernCard = styled(Card)(({ theme }) => ({
  borderRadius: '16px',
  boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
  marginBottom: theme.spacing(3),
}));

// Styled Components - Purple/Blue Gradient Header
const HeaderCard = styled(Card)(({ theme }) => ({
  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
  color: 'white',
  marginBottom: theme.spacing(3),
  borderRadius: '16px',
  boxShadow: '0 8px 32px rgba(102, 126, 234, 0.3)',
}));

export const ProceduresRefactored = forwardRef<unknown, ProceduresProps>(({ patientId, encounterId, externalOpenNewOrder, compact = false }, ref) => {
  // Use custom hook for data management
  const {
    procedures,
    patientMap,
    labIntegrationMap,
    labsLoaded,
    loading,
    addProcedure,
    loadTestDefinitionsByOrderType,
  } = useProcedures(patientId);

  // Get current user data
  const { isAdmin, defaultFacility, user } = useUserData();
  const userFacilities = useSelector(selectUserFacilities);

  // Form state
  const [openNewOrder, setOpenNewOrder] = useState(false);
  const [selectedPatient, setSelectedPatient] = useState<Patient | null>(null);
  const [modalMode, setModalMode] = useState<'edit' | 'view'>('edit');
  const [snackbar, setSnackbar] = useState<{open: boolean, message: string, severity: 'success'|'error'}>({open: false, message: '', severity: 'success'});
  const [showImagingReport, setShowImagingReport] = useState<boolean>(false);
  const [showResendConfirm, setShowResendConfirm] = useState(false);
  const [resendType, setResendType] = useState<'lab' | 'imaging' | null>(null);
  const [showReferralForm, setShowReferralForm] = useState(false);
  const [showRequisitionForm, setShowRequisitionForm] = useState(false);

  // Form fields (using the utility for initial state)
  const [formState, setFormState] = useState<FormState>(getInitialFormState());
  
  // Dynamic test definitions based on order type
  const [orderTypeTestDefinitions, setOrderTypeTestDefinitions] = useState<TestDefinition[]>([]);
  
  // Facility state
  const [assignedFacilities, setAssignedFacilities] = useState<any[]>([]);

  // User state
  const [currentUserName, setCurrentUserName] = useState<string>('');



  // Destructure form state for easier access
  const {
    orderType, orderCode, selectedTestCodes, priority, status, dateOrdered, orderingProvider, orderingFacility,
    selectedLabIntegrationId, notes, specimenType, fastingRequired,
    collectionTime, bodySite, laterality, imagingReport, dicomFile,
    selectedCheckups, followUpNotes, showFollowUp, requisitionId, selectedExternalProvider
  } = formState;

  // Dynamically get status options based on orderType
  // Provide default status options when no order type is selected
  const statusOptions = ORDER_TYPE_STATUS_FLOW[orderType] || [
    'Saved as Draft',
    'Pending',
    'Ordered',
    'Completed',
    'Cancelled'
  ];

  useEffect(() => {
    if (openNewOrder && !patientId) {
      // Note: loadAllPatients functionality removed - patients should be loaded through other means
    }
  }, [openNewOrder, patientId]);

  // Load current user information
  const loadCurrentUser = useCallback(async () => {
    try {
      if (user?.name) {
        setCurrentUserName(user.name);
      } else if (user?.email) {
        setCurrentUserName(user.email);
      }
    } catch (error) {
      console.error('Error loading current user:', error);
    }
  }, [user]);



  

  // Load facilities based on ordering provider
  const loadFacilities = useCallback(async () => {
    try {
      // Get all facilities from service layer
      const facilities = await getAllFacilities();
      
      let assignedFacilities = [];
      
      // Check if user is admin
      if (isAdmin) {
        // Admin users have access to all facilities
        assignedFacilities = facilities;
      } else {
        // Non-admin users only get their assigned facilities
        const userFacilityIds = (userFacilities || []).map((uf: any) => uf.facilityId);
        
        assignedFacilities = facilities.filter((f: any) => userFacilityIds.includes(f.id));
      }
      
      setAssignedFacilities(assignedFacilities);
    } catch (error) {
      console.error('Error loading facilities:', error);
    }
  }, [isAdmin, userFacilities]);

  // Load facilities and user information when component mounts
  useEffect(() => {
    loadFacilities();
    loadCurrentUser();
  }, [loadFacilities, loadCurrentUser]); // Re-load when user role changes

  // Update form state when current user name is loaded
  useEffect(() => {
    if (currentUserName && !openNewOrder) {
      // Only update if this is the initial state (orderingProvider is still the default)
      if (formState.orderingProvider === 'Dr. John Doe') {
        setFormState(prev => ({ ...prev, orderingProvider: currentUserName }));
      }
    }
  }, [currentUserName, openNewOrder, formState.orderingProvider]);

  // Update form state when user default facility is loaded
  useEffect(() => {
    if (defaultFacility?.id && !openNewOrder) {
      // Only update if the form doesn't already have a facility set
      if (!formState.orderingFacility) {
        setFormState(prev => ({ ...prev, orderingFacility: defaultFacility.id }));
      }
    }
  }, [defaultFacility, openNewOrder, formState.orderingFacility]);

 


  // Handle order type change and load test definitions dynamically
  const handleOrderTypeChange = async (newOrderType: string) => {
    setFormState(prev => ({ 
      ...prev, 
      orderType: newOrderType,
      orderCode: null,
      selectedTestCodes: []
    }));

    if (newOrderType) {
      console.log('Loading test definitions for order type:', newOrderType);
      const testDefs = await loadTestDefinitionsByOrderType(newOrderType);
      console.log('Loaded test definitions:', testDefs);
      setOrderTypeTestDefinitions(testDefs);
    } else {
      setOrderTypeTestDefinitions([]);
    }
  };

  const handleCreateOrder = async (statusArg?: 'draft' | 'signed' | 'send' | 'checkup') => {
    console.log('handleCreateOrder called with:', statusArg, 'orderType:', orderType, 'status:', status);
    const usedPatientId = patientId || selectedPatient?.id;
    
    // For draft saves, we allow saving even without order type, but patient is still required
    if (!usedPatientId) {
      setSnackbar({open: true, message: 'Please select a patient first.', severity: 'error'});
      return;
    }
    
    // For non-draft operations, order type is required
    if (statusArg !== 'draft' && !orderType) {
      setSnackbar({open: true, message: 'Please select an order type.', severity: 'error'});
      return;
    }

    // Checkup workflow: create multiple orders, do not transmit
    if (orderType === OrderType.PHYSICAL_EXAM && statusArg === 'checkup') {
      await Promise.all((selectedCheckups || []).map(async (code) => {
        const checkup = CHECKUP_OPTIONS.find(opt => opt.code === code);
        const statusFlow = ORDER_TYPE_STATUS_FLOW[orderType] || [];
        const orderedStatus = statusFlow[1] || 'Ordered';
        const procedureData: any = {
          patientId: usedPatientId,
          facilityId: defaultFacility?.id, // Include the required facilityId
          orderDate: new Date(dateOrdered),
          testType: orderType,
          testCode: code,
          labIntegrationId: selectedLabIntegrationId,
          notes,
          status: orderedStatus,
          orderingProvider,
          orderingFacility,
          checkupLabel: checkup?.label || code,
        };
        return await procedureService.createProcedure(procedureData);
      }));
      
      setSnackbar({open: true, message: 'Checkup orders created!', severity: 'success'});
      setOpenNewOrder(false);
      resetForm();
      return;
    }

    // Build procedure data for all other types
    const statusFlow = ORDER_TYPE_STATUS_FLOW[orderType] || [];
    let statusToSet = status as string;
    if (statusArg === 'draft') {
      statusToSet = statusFlow[0] || 'Saved as Draft';
    } else if (statusArg === 'signed') {
      statusToSet = statusFlow[1] || 'Pending';
    } else if (statusArg === 'send') {
      statusToSet = statusFlow[2] || 'Ordered';
    }

    const procedureData: any = {
      patientId: usedPatientId,
      facilityId: defaultFacility?.id, // Include the required facilityId
      encounterId: encounterId || undefined, // Include encounterId if available
      orderDate: new Date(dateOrdered),
      testType: orderType || 'Generic Procedure', // Default to Generic Procedure for draft saves
      testCode: orderCode ? ('code' in orderCode ? orderCode.code : orderCode.id) : '',
      labIntegrationId: selectedLabIntegrationId,
      notes,
      status: statusToSet,
      orderingProvider,
      orderingFacility,
      priority,
      requisitionId: requisitionId, // Store the requisition ID with the order
    };

    // Add type-specific fields
    if (orderType === OrderType.LAB) {
      procedureData.specimenType = specimenType;
      procedureData.fastingRequired = fastingRequired;
      procedureData.collectionTime = collectionTime;
    }
    if (orderType === OrderType.IMAGING && statusArg === 'send') {
      procedureData.bodySite = bodySite;
      procedureData.laterality = laterality;
      setShowImagingReport(true);
    }
    if (orderType === OrderType.GENERIC && statusArg === 'signed') {
      procedureData.followUpNotes = followUpNotes;
      setFormState(prev => ({ ...prev, showFollowUp: true }));
    }
    if (orderType === OrderType.REFERRAL && selectedExternalProvider) {
      procedureData.externalProvider = {
        npi: selectedExternalProvider.number,
        name: selectedExternalProvider.enumeration_type === 'NPI-1' 
          ? `${selectedExternalProvider.basic.first_name || ''} ${selectedExternalProvider.basic.last_name || ''}`.trim()
          : selectedExternalProvider.basic.name,
        type: selectedExternalProvider.enumeration_type === 'NPI-1' ? 'Individual' : 'Organization',
        specialty: selectedExternalProvider.taxonomies?.[0]?.desc || '',
        address: selectedExternalProvider.addresses?.[0] ? {
          address_1: selectedExternalProvider.addresses[0].address_1,
          address_2: selectedExternalProvider.addresses[0].address_2,
          city: selectedExternalProvider.addresses[0].city,
          state: selectedExternalProvider.addresses[0].state,
          postal_code: selectedExternalProvider.addresses[0].postal_code,
          telephone_number: selectedExternalProvider.addresses[0].telephone_number,
        } : null
      };
    }

    // Create the procedure
    const newProcedure = await procedureService.createProcedure(procedureData);
    addProcedure(newProcedure);

    // Transmit to lab for Laboratory Test or Imaging (but not for draft saves)
    if ((orderType === OrderType.LAB || orderType === OrderType.IMAGING) && statusArg !== 'draft') {
      try {
        // Use selected lab integration if available
        if (selectedLabIntegrationId) {
          // Get basic patient data from patientMap - backend will fetch full details if needed
          const patient = patientMap[usedPatientId];
          const patientData = patient ? {
            id: usedPatientId,
            name: patient.name,
            dateOfBirth: patient.dateOfBirth,
          } : { id: usedPatientId };

          // Send via integration router - uses lab config for protocol (SFTP/API) and format (HL7/FHIR)
          const result = await sendOrderViaIntegration(newProcedure, selectedLabIntegrationId, patientData);
          
          if (result.success) {
            const protocol = result.protocol || 'configured protocol';
            const format = result.dataFormat || 'configured format';
            setSnackbar({
              open: true, 
              message: `Order sent to lab via ${protocol} (${format})!`, 
              severity: 'success'
            });
          } else {
            setSnackbar({
              open: true, 
              message: 'Order created, but failed to send to lab: ' + (result.error || 'Unknown error'), 
              severity: 'error'
            });
          }
        } else {
          // Fallback to legacy SFTP HL7 send if no lab integration selected
          const idToken = await getAuthToken();
          if (idToken) {
            const result = await sendHL7Order(newProcedure, idToken);
            if (result.success) {
              setSnackbar({open: true, message: 'Order sent to lab via SFTP!', severity: 'success'});
            } else {
              setSnackbar({open: true, message: 'Order created, but failed to send to lab: ' + (result.error || 'Unknown error'), severity: 'error'});
            }
          }
        }
      } catch (err: any) {
        setSnackbar({open: true, message: 'Order created, but send failed: ' + (err.message || err), severity: 'error'});
      }
    } else {
      const message = statusArg === 'draft' ? 'Draft saved successfully!' : 'Order created!';
      setSnackbar({open: true, message, severity: 'success'});
    }

    setOpenNewOrder(false);
    resetForm();
  };

  const resetForm = useCallback(() => {
    const initialState = getInitialFormState();
    // Set the current user as the default ordering provider
    if (currentUserName) {
      initialState.orderingProvider = currentUserName;
    }
    // Set the user's default facility as the default ordering facility
    if (defaultFacility?.id) {
      initialState.orderingFacility = defaultFacility.id;
    }
    setFormState(initialState);
    setSelectedPatient(null);
    setShowImagingReport(false);
    setResendType(null);
    setOrderTypeTestDefinitions([]);
  }, [currentUserName, defaultFacility]);

  const handleRowClick = async (params: any) => {
    const order = params.row;
    
    // Load test definitions for this order type first
    let testDefs: TestDefinition[] = [];
    if (order.testType) {
      testDefs = await loadTestDefinitionsByOrderType(order.testType);
      setOrderTypeTestDefinitions(testDefs);
    }
    
    // Find the matching test definition for the order's test code
    let matchingTestDef: TestDefinition | null = null;
    if (order.testCode && testDefs.length > 0) {
      matchingTestDef = testDefs.find(def => 
        def.codeValue === order.testCode || 
        def.id === order.testCode
      ) || null;
    }
    
    const initialState = getInitialFormState();
    setFormState({
      ...initialState,
      orderType: order.testType || '',
      orderCode: matchingTestDef || (order.testCode ? { code: order.testCode, description: order.description || '' } : null),
      selectedTestCodes: matchingTestDef ? [matchingTestDef] : [],
      priority: order.priority || 'Routine',
      status: (ORDER_TYPE_STATUS_FLOW[order.testType]?.includes(order.status) ? order.status : ORDER_TYPE_STATUS_FLOW[order.testType]?.[0]) || '',
      dateOrdered: getDateStringFromOrderDate(order.orderDate),
      selectedLabIntegrationId: order.labIntegrationId || '',
      notes: order.notes || '',
      orderingProvider: order.orderingProvider || '',
      orderingFacility: order.orderingFacility || '',
      specimenType: order.specimenType || '',
      fastingRequired: !!order.fastingRequired,
      collectionTime: order.collectionTime || '',
      bodySite: order.bodySite || '',
      laterality: order.laterality || '',
      selectedCheckups: order.selectedCheckups || [],
      // Keep the existing requisition ID if it exists, otherwise use the one from initial state
      requisitionId: order.requisitionId || initialState.requisitionId,
    });
    
    if (order.patientId) {
      setSelectedPatient({ 
        id: order.patientId, 
        name: patientMap[order.patientId]?.name || '', 
        dateOfBirth: patientMap[order.patientId]?.dateOfBirth || '' 
      });
    }
    
    setModalMode('view');
    setOpenNewOrder(true);
  };

  const handlePrintRequisition = () => {
    // Get current patient data
    const currentPatientId = patientId || selectedPatient?.id;
    const currentPatient = currentPatientId ? patientMap[currentPatientId] : null;
    
    // Get lab integration data
    const labIntegration = selectedLabIntegrationId ? labIntegrationMap[selectedLabIntegrationId] : null;
    
    // Use the stored requisition ID from form state (already generated once)
    
    // Ensure we have valid dates
    const currentDate = new Date().toISOString().split('T')[0]; // YYYY-MM-DD format
    const orderDate = dateOrdered || currentDate;
    
    // Ensure patient DOB is valid or provide a fallback
    let patientDOB = currentPatient?.dateOfBirth || '';
    if (!patientDOB || patientDOB === 'Invalid date') {
      patientDOB = 'Not provided';
    }
    
    // Prepare order data for requisition form
    const orderData = {
      patientId: currentPatientId || '',
      patientName: currentPatient?.name || 'Unknown Patient',
      patientDOB: patientDOB,
      patientGender: 'Not specified', // Would need to be added to patient data
      patientPhone: 'Not provided', // Would need to be added to patient data
      patientEmail: 'Not provided', // Would need to be added to patient data
      patientMRN: 'Not assigned', // Would need to be added to patient data
      patientInsurance: 'Not provided', // Would need to be added to patient data
      patientPolicyNumber: 'Not provided', // Would need to be added to patient data
      orderingProvider: orderingProvider || 'Not specified',
      providerNPI: 'Not provided', // Would need to be added to provider data
      providerClinic: 'Not specified', // Would need to be added to provider data
      providerPhone: 'Not provided', // Would need to be added to provider data
      providerEmail: 'Not provided', // Would need to be added to provider data
      labIntegrationId: selectedLabIntegrationId || '',
      labName: labIntegration || 'Not specified',
      labPhone: 'Not provided', // Would need to be added to lab integration data
      labFax: 'Not provided', // Would need to be added to lab integration data
      labAddress: 'Not provided', // Would need to be added to lab integration data
      orderDate: orderDate,
      requisitionId: requisitionId,
      orderType: orderType || '',
      orderCode: orderCode ? ('code' in orderCode ? orderCode.code : orderCode.id) : '',
      orderDescription: orderCode ? ('description' in orderCode ? orderCode.description : orderCode.name) : '',
      specimenType: specimenType || undefined,
      fastingRequired: fastingRequired,
      collectionTime: collectionTime || undefined,
      bodySite: bodySite || undefined,
      laterality: laterality || undefined,
      notes: notes || undefined,
      icd10Codes: [], // Would need to be added to order data
      reasonForTest: notes || undefined,
    };
    
    // Store order data in component state for the requisition form
    setFormState(prev => ({ ...prev, requisitionOrderData: orderData }));
    setShowRequisitionForm(true);
  };

  const handlePrintReferral = () => {
    setShowReferralForm(true);
  };

  const handleResend = (type: 'lab' | 'imaging') => {
    setResendType(type);
    setShowResendConfirm(true);
  };

  const handleConfirmResend = async () => {
    setShowResendConfirm(false);
    if (resendType === 'lab' || resendType === 'imaging') {
      const usedPatientId = patientId || selectedPatient?.id;
      const procedureData: any = {
        patientId: usedPatientId,
        encounterId: encounterId || undefined, // Include encounterId if available
        orderDate: new Date(dateOrdered),
        testType: orderType,
        testCode: orderCode ? ('code' in orderCode ? orderCode.code : orderCode.id) : '',
        labIntegrationId: selectedLabIntegrationId,
        notes,
        status,
        orderingProvider,
        orderingFacility,
        priority,
      };

      if (orderType === OrderType.LAB) {
        procedureData.specimenType = specimenType;
        procedureData.fastingRequired = fastingRequired;
        procedureData.collectionTime = collectionTime;
      }
      if (orderType === OrderType.IMAGING) {
        procedureData.bodySite = bodySite;
        procedureData.laterality = laterality;
      }

      try {
        // Use selected lab integration if available
        if (selectedLabIntegrationId && usedPatientId) {
          // Get basic patient data from patientMap - backend will fetch full details if needed
          const patient = patientMap[usedPatientId];
          const patientData = patient ? {
            id: usedPatientId,
            name: patient.name,
            dateOfBirth: patient.dateOfBirth,
          } : { id: usedPatientId };

          // Send via integration router - uses lab config for protocol (SFTP/API) and format (HL7/FHIR)
          const result = await sendOrderViaIntegration(procedureData, selectedLabIntegrationId, patientData);
          
          if (result.success) {
            const protocol = result.protocol || 'configured protocol';
            const format = result.dataFormat || 'configured format';
            setSnackbar({
              open: true, 
              message: `Order resent via ${protocol} (${format})!`, 
              severity: 'success'
            });
          } else {
            setSnackbar({open: true, message: 'Order resend failed: ' + (result.error || 'Unknown error'), severity: 'error'});
          }
        } else {
          // Fallback to legacy SFTP HL7 send if no lab integration selected
          const idToken = await getAuthToken();
          if (idToken) {
            const result = await sendHL7Order(procedureData, idToken);
            if (result.success) {
              setSnackbar({
                open: true, 
                message: resendType === 'lab' ? 'Order resent to lab via SFTP!' : 'Order resent to imaging center!', 
                severity: 'success'
              });
            } else {
              setSnackbar({open: true, message: 'Order resend failed: ' + (result.error || 'Unknown error'), severity: 'error'});
            }
          }
        }
      } catch (err: any) {
        setSnackbar({open: true, message: 'Order resend failed: ' + (err.message || err), severity: 'error'});
      }
    }
    setResendType(null);
  };

  const handleCancelResend = () => {
    setShowResendConfirm(false);
    setResendType(null);
  };

  // Imaging order validation
  const isImagingOrderValid = Boolean((patientId || selectedPatient?.id) && orderCode && bodySite.trim() && laterality.trim() && selectedLabIntegrationId);

  // Expose openNewOrder handler to parent if externalOpenNewOrder is provided
  useEffect(() => {
    if (externalOpenNewOrder) {
      externalOpenNewOrder(() => {
        resetForm();
        setModalMode('edit');
        setOpenNewOrder(true);
      });
    }
  }, [externalOpenNewOrder, resetForm]);

  if (!labsLoaded) return (
    <Box sx={{ p: 4, background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)', minHeight: '100vh' }}>
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <Typography variant="h6" color="text.secondary">Loading facility orders...</Typography>
      </Box>
    </Box>
  );

  return (
    <Box sx={{ 
      p: compact ? 2 : 4, 
      background: compact ? 'transparent' : 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)', 
      minHeight: compact ? 'auto' : '100vh' 
    }}>
      {!compact && (
        <HeaderCard>
          <CardContent sx={{ py: 1.5, px: 3, '&:last-child': { pb: 1.5 } }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Box>
                <Typography variant="h4" sx={{ fontWeight: 700, color: 'white', mb: 1 }}>
                  Orders & Procedures
                </Typography>
                <Typography variant="body1" sx={{ color: 'rgba(255, 255, 255, 0.8)' }}>
                  Manage laboratory orders, imaging studies, and procedures
                </Typography>
              </Box>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => {
                  resetForm();
                  setModalMode('edit');
                  setOpenNewOrder(true);
                }}
                sx={{
                  backgroundColor: 'rgba(255, 255, 255, 0.2)',
                  '&:hover': {
                    backgroundColor: 'rgba(255, 255, 255, 0.3)',
                  },
                  borderRadius: '12px',
                  textTransform: 'none',
                  fontWeight: 600,
                  px: 3,
                  py: 1.5,
                }}
              >
                Add Order
              </Button>
            </Box>
          </CardContent>
        </HeaderCard>
      )}
      

      
      <ModernCard sx={{ 
        marginBottom: compact ? 1 : 3,
        boxShadow: compact ? '0 1px 4px rgba(0, 0, 0, 0.1)' : '0 4px 20px rgba(0, 0, 0, 0.08)'
      }}>
        <CardContent sx={{ p: compact ? 1.5 : 2 }}>
          <LabOrdersGrid 
            procedures={procedures} 
            patientMap={patientMap} 
            labIntegrationMap={labIntegrationMap} 
            onRowClick={handleRowClick} 
            compact={compact}
            onEditDraft={async (order) => {
              // Load test definitions for this order type first
              let testDefs: TestDefinition[] = [];
              if (order.testType) {
                testDefs = await loadTestDefinitionsByOrderType(order.testType);
                setOrderTypeTestDefinitions(testDefs);
              }
              
              // Find the matching test definition for the order's test code
              let matchingTestDef: TestDefinition | null = null;
              if (order.testCode && testDefs.length > 0) {
                matchingTestDef = testDefs.find(def => 
                  def.codeValue === order.testCode || 
                  def.id === order.testCode
                ) || null;
              }
              
              const initialState = getInitialFormState();
                  setFormState({
      ...initialState,
      orderType: order.testType || '',
      orderCode: matchingTestDef || (order.testCode ? { code: order.testCode, description: order.description || '' } : null),
      selectedTestCodes: matchingTestDef ? [matchingTestDef] : [],
      priority: order.priority || 'Routine',
      status: (ORDER_TYPE_STATUS_FLOW[order.testType]?.includes(order.status) ? order.status : ORDER_TYPE_STATUS_FLOW[order.testType]?.[0]) || '',
      dateOrdered: getDateStringFromOrderDate(order.orderDate),
      selectedLabIntegrationId: order.labIntegrationId || '',
      notes: order.notes || '',
      orderingProvider: order.orderingProvider || '',
      orderingFacility: order.orderingFacility || '',
      specimenType: order.specimenType || '',
      fastingRequired: !!order.fastingRequired,
      collectionTime: order.collectionTime || '',
      bodySite: order.bodySite || '',
      laterality: order.laterality || '',
      selectedCheckups: order.selectedCheckups || [],
      // Keep the existing requisition ID if it exists, otherwise use the one from initial state
      requisitionId: order.requisitionId || initialState.requisitionId,
    });
              
              // Load test definitions for this order type
              if (order.testType) {
                const testDefs = await loadTestDefinitionsByOrderType(order.testType);
                setOrderTypeTestDefinitions(testDefs);
              }
              
              if (order.patientId) {
                setSelectedPatient({ 
                  id: order.patientId, 
                  name: patientMap[order.patientId]?.name || '', 
                  dateOfBirth: patientMap[order.patientId]?.dateOfBirth || '' 
                });
              }
              setModalMode('edit');
              setOpenNewOrder(true);
            }}
          />
        </CardContent>
      </ModernCard>

      <Dialog 
        open={openNewOrder} 
        onClose={() => setOpenNewOrder(false)} 
        maxWidth="md" 
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '16px',
            boxShadow: '0 24px 48px rgba(0, 0, 0, 0.15)',
          }
        }}
      >
        <DialogTitle sx={{ pb: 1, fontWeight: 600 }}>
          {modalMode === 'edit' ? 'New Lab Order' : 'Order Details'}
        </DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <Box component="form" sx={{ mt: 1 }}>
            {/* Order Type at the very top */}
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>Order Type</InputLabel>
              <Select
                value={orderType}
                label="Order Type"
                onChange={e => modalMode === 'edit' && handleOrderTypeChange(e.target.value)}
                disabled={modalMode === 'view'}
              >
                {ORDER_TYPES.map((type: string) => (
                  <MenuItem key={type} value={type}>{type}</MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Patient and Order Date row */}
            <Grid container spacing={2} sx={{ mb: 2 }}>
              {/* Patient Selection (if not in patient context) */}
              {!patientId && (
                <Grid item xs={12} md={6}>
                  <Autocomplete
                    options={Object.entries(patientMap || {}).map(([id, patient]) => ({
                      id,
                      name: patient.name,
                      dateOfBirth: patient.dateOfBirth,
                      label: `${patient.name}${patient.dateOfBirth ? ` (${patient.dateOfBirth})` : ''}`
                    }))}
                    getOptionLabel={(option) => option.label}
                    value={selectedPatient ? {
                      id: selectedPatient.id,
                      name: selectedPatient.name,
                      dateOfBirth: selectedPatient.dateOfBirth,
                      label: `${selectedPatient.name}${selectedPatient.dateOfBirth ? ` (${selectedPatient.dateOfBirth})` : ''}`
                    } : null}
                    onChange={(event, newValue) => {
                      if (modalMode === 'view') return;
                      if (newValue) {
                        setSelectedPatient({ 
                          id: newValue.id, 
                          name: newValue.name, 
                          dateOfBirth: newValue.dateOfBirth 
                        });
                      } else {
                        setSelectedPatient(null);
                      }
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Patient"
                        placeholder="Search for patient..."
                      />
                    )}
                    disabled={modalMode === 'view'}
                    filterOptions={(options, { inputValue }) => {
                      const searchTerm = inputValue.toLowerCase();
                      return options.filter(option => 
                        option.name.toLowerCase().includes(searchTerm) ||
                        (option.dateOfBirth && option.dateOfBirth.toLowerCase().includes(searchTerm))
                      );
                    }}
                    isOptionEqualToValue={(option, value) => option.id === value.id}
                    noOptionsText="No patients found"
                    loading={loading}
                  />
                </Grid>
              )}
              <Grid item xs={12} md={patientId ? 12 : 6}>
                <DatePicker
                  label="Order Date"
                  value={dateOrdered ? new Date(dateOrdered) : null}
                  onChange={(date) => modalMode === 'edit' && setFormState(prev => ({ ...prev, dateOrdered: date ? date.toISOString().split('T')[0] : '' }))}
                  readOnly={modalMode === 'view'}
                  slotProps={{
                    textField: { fullWidth: true }
                  }}
                />
              </Grid>
            </Grid>

            {/* Ordering Provider and Ordering Facility in one row */}
            <Grid container spacing={2} sx={{ mb: 2 }}>
              <Grid item xs={12} md={6}>
                <TextField
                  label="Ordering Provider"
                  value={orderingProvider}
                  onChange={e => modalMode === 'edit' && setFormState(prev => ({ ...prev, orderingProvider: e.target.value }))}
                  fullWidth
                  InputProps={{ readOnly: modalMode === 'view' }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>Ordering Facility</InputLabel>
                  <Select
                    value={orderingFacility}
                    label="Ordering Facility"
                    onChange={e => modalMode === 'edit' && setFormState(prev => ({ ...prev, orderingFacility: e.target.value }))}
                    disabled={modalMode === 'view'}
                  >
                    {(assignedFacilities || []).map(facility => (
                      <MenuItem key={facility.id} value={facility.id}>
                        {facility.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>

            {/* Priority and Status in one row */}
            <Grid container spacing={2} sx={{ mb: 2 }}>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>Priority</InputLabel>
                  <Select
                    value={priority}
                    label="Priority"
                    onChange={e => modalMode === 'edit' && setFormState(prev => ({ ...prev, priority: e.target.value }))}
                    disabled={modalMode === 'view'}
                  >
                    {PRIORITY_OPTIONS.map(opt => (
                      <MenuItem key={opt} value={opt}>{opt}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>Status</InputLabel>
                  <Select
                    value={status}
                    label="Status"
                    onChange={e => modalMode === 'edit' && setFormState(prev => ({ ...prev, status: e.target.value }))}
                    disabled={modalMode === 'view'}
                  >
                    {statusOptions.map(opt => (
                      <MenuItem key={opt} value={opt}>{opt}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>

            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>Lab</InputLabel>
              <Select
                value={selectedLabIntegrationId}
                label="Lab"
                onChange={e => modalMode === 'edit' && setFormState(prev => ({ ...prev, selectedLabIntegrationId: e.target.value }))}
                disabled={modalMode === 'view'}
              >
                {Object.entries(labIntegrationMap || {}).map(([id, name]) => (
                  <MenuItem key={id} value={id}>{name}</MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Test Code Selection - Dynamically Loaded by Order Type */}
            {orderType && (
              <Autocomplete
                multiple
                options={orderTypeTestDefinitions || []}
                getOptionLabel={(option) => `${option.codeValue} - ${option.name}`}
                value={selectedTestCodes || []}
                onChange={(_, newValue) => {
                  if (modalMode === 'edit') {
                    setFormState(prev => ({ 
                      ...prev, 
                      selectedTestCodes: newValue,
                      orderCode: newValue.length > 0 ? newValue[0] : null 
                    }));
                  }
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Test Code"
                    placeholder="Search and select test codes..."
                    fullWidth
                    sx={{ mb: 2 }}
                    InputProps={{
                      ...params.InputProps,
                      readOnly: modalMode === 'view'
                    }}
                  />
                )}
                renderOption={(props, option, { selected }) => {
                  const { key, ...otherProps } = props;
                  return (
                    <li key={key} {...otherProps}>
                      <Checkbox
                        checked={selected}
                        sx={{ marginRight: 1 }}
                      />
                      <Box>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {option.codeValue} - {option.name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {option.codeType} | {option.testType}
                        </Typography>
                      </Box>
                    </li>
                  );
                }}
                renderTags={(value, getTagProps) =>
                  (value || []).map((option, index) => {
                    const { key, ...tagProps } = getTagProps({ index });
                    return (
                      <Chip
                        key={key}
                        label={`${option.codeValue} - ${option.name}`}
                        {...tagProps}
                        disabled={modalMode === 'view'}
                        size="small"
                      />
                    );
                  })
                }
                disabled={modalMode === 'view'}
                disableCloseOnSelect
                ChipProps={{
                  size: 'small',
                  variant: 'outlined'
                }}
              />
            )}

            {/* Type-specific fields */}
            <OrderTypeFields
              orderType={orderType}
              orderCode={orderCode}
              setOrderCode={(code) => setFormState(prev => ({ ...prev, orderCode: code }))}
              specimenType={specimenType}
              setSpecimenType={(type) => setFormState(prev => ({ ...prev, specimenType: type }))}
              fastingRequired={fastingRequired}
              setFastingRequired={(required) => setFormState(prev => ({ ...prev, fastingRequired: required }))}
              collectionTime={collectionTime}
              setCollectionTime={(time) => setFormState(prev => ({ ...prev, collectionTime: time }))}
              bodySite={bodySite}
              setBodySite={(site) => setFormState(prev => ({ ...prev, bodySite: site }))}
              laterality={laterality}
              setLaterality={(lat) => setFormState(prev => ({ ...prev, laterality: lat }))}
              selectedCheckups={selectedCheckups}
              setSelectedCheckups={(checkups) => setFormState(prev => ({ ...prev, selectedCheckups: checkups }))}
              testDefinitions={orderTypeTestDefinitions}
              modalMode={modalMode}
              CHECKUP_OPTIONS={CHECKUP_OPTIONS}
              selectedExternalProvider={selectedExternalProvider}
              setSelectedExternalProvider={(provider) => setFormState(prev => ({ ...prev, selectedExternalProvider: provider }))}
            />

            <MacroTextBox
              fullWidth
              multiline
              rows={3}
              label="Notes"
              value={notes}
              onChange={(e: any) => setFormState(prev => ({ ...prev, notes: e.target.value }))}
              placeholder="Type /m for macros or @code to expand... (e.g., @lab_orders, @vital_signs)"
              variant="outlined"
              disabled={modalMode === 'view'}
            />
            {showFollowUp && (
              <TextField
                fullWidth
                multiline
                rows={2}
                label="Follow-up Notes"
                value={followUpNotes}
                onChange={e => setFormState(prev => ({ ...prev, followUpNotes: e.target.value }))}
                sx={{ mt: 2 }}
                disabled={modalMode === 'view'}
              />
            )}
          </Box>
        </DialogContent>
        
        <DialogActions sx={{ p: 3, pt: 2 }}>
          <Button 
            onClick={() => setOpenNewOrder(false)}
            sx={{ textTransform: 'none', fontWeight: 500 }}
          >
            Cancel
          </Button>
          
          {/* Print buttons available in both view and edit modes (unless Cancelled or Completed) */}
          {modalMode === 'view' && orderType && status !== ProcedureStatus.CANCELLED && status !== ProcedureStatus.COMPLETED && (
            <>
              {/* Print Requisition for Laboratory Test and Imaging */}
              {([OrderType.LAB, OrderType.IMAGING].includes(orderType as OrderType)) && (
                <Button onClick={handlePrintRequisition}>
                  Print Requisition Form
                </Button>
              )}
              
              {/* Print Referral Form for Referral orders only */}
              {orderType === OrderType.REFERRAL && (
                <Button onClick={handlePrintReferral}>
                  Print Referral Form
                </Button>
              )}
              
              {/* Print Referral for other order types (excluding Referral which has its own button above) */}
              {([OrderType.PHYSICAL_EXAM, OrderType.INTERVENTION, OrderType.PROCEDURE, OrderType.GENERIC, OrderType.RISK_ASSESSMENT].includes(orderType as OrderType)) && (
                <Button onClick={handlePrintReferral}>
                  Print Referral Form
                </Button>
              )}
            </>
          )}
          
          {modalMode === 'edit' && (
            <OrderActions
              orderType={orderType}
              status={status}
              isImagingOrderValid={isImagingOrderValid}
              onSaveDraft={() => handleCreateOrder('draft')}
              onSignAndSend={() => handleCreateOrder('signed')}
              onSendToLab={() => handleCreateOrder('send')}
              onResendLab={() => handleResend('lab')}
              onResendImaging={() => handleResend('imaging')}
              onPrintRequisition={handlePrintRequisition}
              onPrintReferral={handlePrintReferral}
              onCreateCheckupOrders={() => handleCreateOrder('checkup')}
            />
          )}
        </DialogActions>
      </Dialog>

      <ImagingReportDialog
        open={showImagingReport}
        onClose={() => setShowImagingReport(false)}
        imagingReport={imagingReport}
        setImagingReport={(report) => setFormState(prev => ({ ...prev, imagingReport: report }))}
        dicomFile={dicomFile}
        setDicomFile={(file) => setFormState(prev => ({ ...prev, dicomFile: file }))}
      />

      <ResendConfirmDialog
        open={showResendConfirm}
        onClose={handleCancelResend}
        onConfirm={handleConfirmResend}
        resendType={resendType}
      />

      {showReferralForm && (patientId || selectedPatient) && (
        <ReferralForm
          open={showReferralForm}
          onClose={() => setShowReferralForm(false)}
          patient={{
            id: patientId || selectedPatient?.id || '',
            name: patientId ? patientMap[patientId]?.name || 'Unknown Patient' : selectedPatient?.name || 'Unknown Patient',
            dateOfBirth: patientId ? patientMap[patientId]?.dateOfBirth || '' : selectedPatient?.dateOfBirth || '',
            gender: 'Not specified',
            contact: 'Not provided',
            email: 'Not provided',
            address: 'Address not provided',
            mrn: 'Not assigned',
            insurance: 'Not provided',
            policyNumber: 'Not provided',
            primaryLanguage: 'English'
          }}
          referringProvider={{
            name: orderingProvider || 'Dr. Provider Name, MD',
            practice: 'Medical Practice',
            phone: '(555) 123-4567',
            npi: '1234567890',
            email: 'provider@practice.com'
          }}
          receivingProvider={{
            name: selectedExternalProvider 
              ? (selectedExternalProvider.enumeration_type === 'NPI-1' 
                  ? `${selectedExternalProvider.basic.first_name || ''} ${selectedExternalProvider.basic.last_name || ''}`.trim()
                  : selectedExternalProvider.basic.name || 'Unknown Provider')
              : 'Dr. Specialist Name, MD',
            practice: selectedExternalProvider 
              ? (selectedExternalProvider.enumeration_type === 'NPI-1' 
                  ? selectedExternalProvider.taxonomies?.[0]?.desc || 'Medical Practice'
                  : selectedExternalProvider.basic.name || 'Specialty Practice')
              : (orderCode && 'description' in orderCode ? orderCode.description : 'Specialty Practice'),
            phone: selectedExternalProvider?.addresses?.[0]?.telephone_number || '(555) 987-6543',
            npi: selectedExternalProvider?.number || '0987654321',
            email: 'specialist@practice.com' // NPI registry doesn't provide email
          }}
          clinicalDetails={{
            reasonForReferral: notes || 'Clinical referral for further evaluation and treatment.',
            primaryDiagnosis: orderCode && 'description' in orderCode ? orderCode.description : 'Primary diagnosis to be determined',
            pertinentHistory: 'Patient history and relevant clinical information.',
            testsOrdered: orderCode && 'description' in orderCode ? orderCode.description : 'Tests and procedures ordered',
            requestedAction: 'Please provide consultation and follow-up recommendations.'
          }}
          attachments={{
            encounterNote: true,
            growthChart: false,
            ekgReport: false,
            insuranceAuth: false,
            imagingCD: false
          }}
          followUp={{
            callPatient: false,
            emailReport: true,
            faxNote: false
          }}
          notes="Please contact us if additional documentation is needed. This referral is valid for 90 days."
        />
      )}

      <RequisitionForm
        open={showRequisitionForm}
        onClose={() => setShowRequisitionForm(false)}
        orderData={{
          patientId: patientId || selectedPatient?.id || '',
          patientName: patientId ? patientMap[patientId]?.name || 'Unknown Patient' : selectedPatient?.name || 'Unknown Patient',
          patientDOB: patientId ? patientMap[patientId]?.dateOfBirth || '' : selectedPatient?.dateOfBirth || '',
          orderingProvider: orderingProvider || 'Not specified',
          labIntegrationId: selectedLabIntegrationId || '',
          labName: labIntegrationMap[selectedLabIntegrationId] || 'Not specified',
          orderDate: dateOrdered,
          requisitionId: requisitionId,
          orderType: orderType,
          orderCode: orderCode && 'code' in orderCode ? orderCode.code : orderCode?.id || '',
          orderDescription: orderCode && 'description' in orderCode ? orderCode.description : orderCode?.name || '',
        }}
      />

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
});